/**
 * Stage 5 — execution.
 *
 * Automatic tests run here, against the vault, with the request / response /
 * verdict log shown as the evidence. Manual tests show their assignee and
 * progress; the owner can record a result on a tester's behalf. When every
 * test is closed, "Evaluate & write report" moves to stage 6.
 */

import { useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Checkbox,
  Code,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconFileReport,
  IconInfoCircle,
  IconPlayerPlay,
  IconRobot,
  IconUser,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  downloadEvidence,
  evaluateRun,
  executeAllAutomatic,
  executeAutomaticTest,
  ownerAddEvidence,
  ownerManualResult,
  type EvidenceOut,
  type RunOut,
  type TestOut,
} from "@/shared/api/validation";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { ManualResultForm, ResultBadge } from "../ManualResultForm";
import { TEST_STATUS_COLOR, TEST_STATUS_LABEL, fmtDate, riskColor } from "../stageMeta";

interface Props {
  run: RunOut;
  onUpdated: (run: RunOut, jump?: boolean) => void;
}

const CLOSED = ["passed", "failed", "blocked"];

export function ExecutionStage({ run, onUpdated }: Props) {
  const getToken = useAccessToken();
  const [busy, setBusy] = useState<string | null>(null);
  const [allowOpen, setAllowOpen] = useState(false);

  const executing = run.is_owner && run.status === "executing";
  const automatic = run.tests.filter((t) => t.mode === "automatic");
  const manual = run.tests.filter((t) => t.mode === "manual");
  const openAuto = automatic.filter((t) => !CLOSED.includes(t.status));
  const openAll = run.tests.filter((t) => !CLOSED.includes(t.status));

  const runAll = async () => {
    setBusy("all");
    try {
      const fresh = await executeAllAutomatic(await getToken(), run.run_id);
      onUpdated(fresh, false);
      const done = fresh.tests.filter((t) => t.mode === "automatic");
      toastSuccess(
        "Automatic tests executed",
        `${done.filter((t) => t.status === "passed").length} passed · ${done.filter((t) => t.status === "failed").length} failed · ${done.filter((t) => t.status === "blocked").length} blocked`,
      );
    } catch (err) {
      toastApiError("Execution failed", err);
    } finally {
      setBusy(null);
    }
  };

  const runOne = async (t: TestOut) => {
    setBusy(t.test_id);
    try {
      const fresh = await executeAutomaticTest(await getToken(), run.run_id, t.test_id);
      onUpdated(fresh, false);
      const after = fresh.tests.find((x) => x.test_id === t.test_id);
      toastSuccess(`${t.code} executed`, after?.observed_result ?? after?.status ?? "");
    } catch (err) {
      toastApiError(`${t.code} failed to execute`, err);
    } finally {
      setBusy(null);
    }
  };

  const evaluate = async () => {
    setBusy("evaluate");
    try {
      const fresh = await evaluateRun(await getToken(), run.run_id, allowOpen);
      onUpdated(fresh);
      toastSuccess("Report written", `Readiness: ${fresh.readiness ?? "—"}`);
    } catch (err) {
      toastApiError("Could not evaluate the run", err);
    } finally {
      setBusy(null);
    }
  };

  const download = async (e: EvidenceOut) => {
    try {
      const blob = await downloadEvidence(await getToken(), e.evidence_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = e.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastApiError("Could not download evidence", err);
    }
  };

  if (
    run.tests.length === 0 ||
    ["draft", "assessed", "risks_confirmed", "planned", "authored"].includes(run.status)
  ) {
    return (
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />}>
        Execution opens once the tests are approved and testers assigned (Tests tab).
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Text fw={600}>
          {run.tests.length - openAll.length} of {run.tests.length} tests closed
        </Text>
        {executing && (
          <Group gap="xs" align="center">
            <Checkbox
              size="xs"
              label="allow open tests (counts as NO-GO)"
              checked={allowOpen}
              onChange={(e) => setAllowOpen(e.currentTarget.checked)}
            />
            <Tooltip label="Coverage, defects and readiness are computed; the report narrative is AI-drafted">
              <Button
                leftSection={<IconFileReport size={16} />}
                disabled={openAll.length > 0 && !allowOpen}
                loading={busy === "evaluate"}
                onClick={() => void evaluate()}
              >
                Evaluate & write report
              </Button>
            </Tooltip>
          </Group>
        )}
      </Group>

      {automatic.length > 0 && (
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconRobot size={18} />
              <Text fw={600}>Automatic · Veeva API ({automatic.length})</Text>
              <Text size="xs" c="dimmed">
                read-only VQL assertions; request, response and verdict are the evidence
              </Text>
            </Group>
            {executing && (
              <Button
                size="xs"
                leftSection={<IconPlayerPlay size={14} />}
                disabled={openAuto.length === 0}
                loading={busy === "all"}
                onClick={() => void runAll()}
              >
                Run {openAuto.length} open
              </Button>
            )}
          </Group>
          <Accordion variant="separated">
            {automatic.map((t) => (
              <Accordion.Item key={t.test_id} value={t.test_id}>
                <Accordion.Control>
                  <TestRow t={t} />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    {executing && !CLOSED.includes(t.status) && (
                      <Button
                        size="xs"
                        variant="light"
                        w="fit-content"
                        leftSection={<IconPlayerPlay size={14} />}
                        loading={busy === t.test_id}
                        onClick={() => void runOne(t)}
                      >
                        Run {t.code}
                      </Button>
                    )}
                    {t.step_results && t.step_results.length > 0 ? (
                      <ApiLog entries={t.step_results} />
                    ) : (
                      <Stack gap={4}>
                        {t.api_assertions.map((a, i) => (
                          <Code key={i} block>
                            {a.vql}
                          </Code>
                        ))}
                      </Stack>
                    )}
                    {t.observed_result && (
                      <Text size="sm">
                        <ResultBadge value={t.status} /> {t.observed_result}
                        <Text span size="xs" c="dimmed">
                          {" "}
                          · {fmtDate(t.executed_at_utc)} · {(t.duration_ms ?? 0).toFixed(0)} ms
                        </Text>
                      </Text>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Paper>
      )}

      {manual.length > 0 && (
        <Paper withBorder radius="md" p="md">
          <Group gap="xs" mb="sm">
            <IconUser size={18} />
            <Text fw={600}>Manual · assigned testers ({manual.length})</Text>
            <Text size="xs" c="dimmed">
              testers record step results and evidence under “My tests”; the owner may record on
              their behalf
            </Text>
          </Group>
          <Accordion variant="separated">
            {manual.map((t) => (
              <Accordion.Item key={t.test_id} value={t.test_id}>
                <Accordion.Control>
                  <TestRow t={t} />
                </Accordion.Control>
                <Accordion.Panel>
                  <ManualResultForm
                    test={t}
                    onUpload={async (file, note) => {
                      if (!executing) return;
                      try {
                        onUpdated(
                          await ownerAddEvidence(
                            await getToken(),
                            run.run_id,
                            t.test_id,
                            file,
                            note,
                          ),
                          false,
                        );
                        toastSuccess("Evidence attached", file.name);
                      } catch (err) {
                        toastApiError("Could not attach evidence", err);
                      }
                    }}
                    onSubmit={async (input) => {
                      if (!executing) return;
                      try {
                        onUpdated(
                          await ownerManualResult(await getToken(), run.run_id, t.test_id, input),
                          false,
                        );
                        toastSuccess("Result recorded", `${t.code} — ${input.verdict}`);
                      } catch (err) {
                        toastApiError("Could not record the result", err);
                      }
                    }}
                    onDownloadEvidence={(e) => void download(e)}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Paper>
      )}
    </Stack>
  );
}

function TestRow({ t }: { t: TestOut }) {
  return (
    <Group justify="space-between" wrap="nowrap" pr="md">
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <Badge variant="outline" color="gray" size="sm">
          {t.code}
        </Badge>
        <div style={{ minWidth: 0 }}>
          <Text fw={600} size="sm" truncate>
            {t.title}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            {t.mode === "manual"
              ? `tester ${t.assignee_display_name ?? t.assignee_email ?? "unassigned"}`
              : `${t.api_assertions.length} assertion${t.api_assertions.length === 1 ? "" : "s"}`}
            {t.evidence.length
              ? ` · ${t.evidence.length} evidence file${t.evidence.length === 1 ? "" : "s"}`
              : ""}
            {t.executed_at_utc ? ` · ${fmtDate(t.executed_at_utc)}` : ""}
          </Text>
        </div>
      </Group>
      <Group gap="xs" wrap="nowrap">
        {t.gxp_risk && (
          <Badge size="sm" variant="light" color={riskColor(t.gxp_risk)}>
            {t.gxp_risk}
          </Badge>
        )}
        <Badge size="sm" variant="light" color={TEST_STATUS_COLOR[t.status]}>
          {TEST_STATUS_LABEL[t.status]}
        </Badge>
      </Group>
    </Group>
  );
}

function ApiLog({ entries }: { entries: Record<string, unknown>[] }) {
  return (
    <Table withTableBorder verticalSpacing={6}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Query</Table.Th>
          <Table.Th w={260}>Result</Table.Th>
          <Table.Th w={90}>Verdict</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {entries.map((e, i) => {
          const preview = e.records_preview as Record<string, unknown>[] | undefined;
          return (
            <Table.Tr key={i}>
              <Table.Td>
                <Text size="xs">{String(e.description ?? "")}</Text>
                <Code block>{String(e.vql ?? "")}</Code>
                {preview && preview.length > 0 && (
                  <Code block style={{ fontSize: 11, maxHeight: 120, overflow: "auto" }}>
                    {JSON.stringify(preview, null, 1)}
                  </Code>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="xs">{String(e.actual ?? e.error ?? "")}</Text>
                <Text size="xs" c="dimmed">
                  {String(e.at ?? "")}
                </Text>
              </Table.Td>
              <Table.Td>
                <ResultBadge value={String(e.verdict ?? "")} />
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
